import { useCallback, useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';

/* Unterschrift am Tablet (Betreiber 21.09.2026): Canvas mit Pointer-Events
   (Finger, Stift, Maus). Liefert per onChange eine PNG-Data-URL oder null.
   Bewusst ohne Fremdbibliothek.

   03.10.2026 (Unterschrift-Station) gehaertet:
   - ein blosses Antippen gilt nicht mehr als Unterschrift (Mindeststrecke),
   - das Feld vermisst sich neu, wenn sich die Breite aendert (iPad drehen) und zeichnet die
     bisherige Unterschrift massstabsgetreu wieder ein — Finger und Strich bleiben deckungsgleich,
   - die Pixeldichte ist auf 2 begrenzt, damit ein grosses Feld unter der Servergrenze (300 KB) bleibt,
   - `hoehe` fuer ein groesseres Feld auf der Station. */
const MIN_STRECKE = 28;   // CSS-Pixel

export default function Unterschrift({ wert, onChange, label, tid, hoehe = 130 }) {
  const ref = useRef(null);
  const zeichnet = useRef(false);
  const strecke = useRef(wert ? MIN_STRECKE : 0);
  const letzter = useRef(null);
  const stand = useRef(wert ? { bild: wert, b: 0, h: 0 } : null);   // letzter Export + CSS-Masse beim Export
  const [leer, setLeer] = useState(!wert);

  const einrichten = useCallback(() => {
    const c = ref.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    const s = stand.current;
    if (s?.bild) {
      const img = new Image();
      img.onload = () => {
        // massstabsgetreu einpassen (nicht verzerren), linksbuendig, vertikal mittig
        const b = s.b || rect.width; const h = s.h || rect.height;
        const f = Math.min(rect.width / b, rect.height / h, 1);
        ctx.drawImage(img, 0, (rect.height - h * f) / 2, b * f, h * f);
      };
      img.src = s.bild;
    }
  }, []);

  useEffect(() => {
    einrichten();
    const c = ref.current;
    if (!c || typeof ResizeObserver === 'undefined') return undefined;
    let breite = c.getBoundingClientRect().width;
    const ro = new ResizeObserver(() => {
      const b = c.getBoundingClientRect().width;
      if (Math.abs(b - breite) > 1 && !zeichnet.current) { breite = b; einrichten(); }
    });
    ro.observe(c);
    return () => ro.disconnect();
  }, [einrichten]);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  const start = (e) => {
    e.preventDefault();
    zeichnet.current = true;
    ref.current.setPointerCapture?.(e.pointerId);
    const ctx = ref.current.getContext('2d');
    const [x, y] = pos(e);
    letzter.current = [x, y];
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const zieh = (e) => {
    if (!zeichnet.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext('2d');
    const [x, y] = pos(e);
    const [lx, ly] = letzter.current || [x, y];
    strecke.current += Math.hypot(x - lx, y - ly);
    letzter.current = [x, y];
    ctx.lineTo(x, y);
    ctx.stroke();
    if (leer && strecke.current >= MIN_STRECKE) setLeer(false);
  };
  const ende = (e) => {
    if (!zeichnet.current) return;
    zeichnet.current = false;
    e.preventDefault();
    if (strecke.current < MIN_STRECKE) return;          // Antippen oder Punkt: keine Unterschrift
    const r = ref.current.getBoundingClientRect();
    stand.current = { bild: ref.current.toDataURL('image/png'), b: r.width, h: r.height };
    onChange(stand.current.bild);
  };
  const loeschen = () => {
    const c = ref.current;
    const ctx = c.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
    strecke.current = 0;
    stand.current = null;
    setLeer(true);
    onChange(null);
  };

  return (
    <div className="space-y-1" data-testid={tid}>
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
        <button type="button" onClick={loeschen} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid={tid ? `${tid}-loeschen` : undefined}><Eraser className="w-3 h-3" /> löschen</button>
      </div>
      <div className="relative rounded-xl border border-border/60 bg-white overflow-hidden" style={{ height: hoehe }}>
        <canvas ref={ref} className="w-full h-full touch-none cursor-crosshair block"
          onPointerDown={start} onPointerMove={zieh} onPointerUp={ende} onPointerCancel={ende} onPointerLeave={ende} />
        {leer && <span className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground/60 pointer-events-none">Hier unterschreiben</span>}
        <div className="absolute left-4 right-4 bottom-6 border-b border-dashed border-border pointer-events-none" />
      </div>
    </div>
  );
}
